import { AppCore } from "../api/core/AppCore";
import { CommonGameEvent } from "../game/controller/event/CommonGameEvent";
import { AssetManager } from "../game/manager/AssetManager";
import { Renderer } from "../game/renderer/Renderer";
import { EventHandler } from "../util/event/EventHandler";
import { EventObject } from "../util/event/EventObject";
import { Logger } from "../util/log/Logger";
import { AssetLoader } from "./asset/AssetLoader";
import { ComponentLoader } from "./component/ComponentLoader";
import { ComponentUnloader } from "./component/ComponentUnloader";
import { LoaderEvent } from "./event/LoaderEvent";
import { ConfigRequest } from "./request/ConfigRequest";
/*
 * AppLoader.ts
 */
export class AppLoader {
    constructor(url, version) {
        this.versionPrefix = version ? version + "/" : "";
        this.url = url;
        this.create();
        this.addEventListeners();
    }
    create() {
        this.core = AppCore.getInstance();
        this.assetManager = AssetManager.getInstance();
        this.renderer = Renderer.getInstance();
        this.componentUnloader = new ComponentUnloader();
        this.reset();
        Logger.addObjectRef(this, "AppLoader");
    }
    onPreloaderReady(handler, scope) {
        this.preloaderReadyHandler = new EventHandler(handler, scope);
    }
    onPreloaderComplete(handler, scope) {
        this.preloaderCompleteHandler = new EventHandler(handler, scope);
    }
    onLoaderReady(handler, scope) {
        this.loaderReadyHandler = new EventHandler(handler, scope);
    }
    onLoaderProgress(handler, scope) {
        this.loaderProgressHandler = new EventHandler(handler, scope);
    }
    onLoaderComplete(handler, scope) {
        this.loaderCompleteHandler = new EventHandler(handler, scope);
    }
    destroy() {
        this.reset();
        this.core = null;
        this.assetManager = null;
        this.renderer = null;
        this.componentLoader = null;
        this.componentUnloader = null;
    }
    reset() {
        this.progress = 0;
        this.preloaderComplete = false;
        this.loaderReady = false;
        this.gameComplete = false;
        this.loadersComplete = false;
    }
    resetLoader() {
        if (this.componentLoader) {
            this.componentLoader.dispose();
            this.componentLoader = null;
        }
    }
    addEventListeners() {
        this.listenCore(LoaderEvent.PRELOADER_COMPLETE, this.handlePreloaderComplete, this);
    }
    load() {
        this.request = new ConfigRequest(this.url, this.versionPrefix);
        this.request.onComplete(this.handleConfigComplete, this);
        this.request.load();
    }
    handleConfigComplete() {
        this.componentsById = this.request.getComponentsById();
        this.componentsByKey = this.request.getComponentsByKey();
        this.components = this.componentsByKey.getValues().concat();
        let values = this.request.getFiles();
        for (let i = 0; i < values.length; i++) {
            this.assetManager.addPath(values[i]);
        }
        this.loadPreloader();
    }
    loadPreloader() {
        if (this.hasComponent(AppLoader.PRELOADER)) {
            this.dispatchKPI("TITAN_STATE", "BRAND_LOGO_DOWNLOAD_STARTED", null);
            this.componentLoader = new ComponentLoader(this.request, AppLoader.PRELOADER);
            this.componentLoader.onComplete(this.handlePreloaderReady, this);
            this.componentLoader.load([this.getComponent(AppLoader.PRELOADER)]);
        }
        else {
            this.handlePreloaderReady();
            this.handlePreloaderComplete();
        }
    }
    handlePreloaderReady() {
        this.dispatchKPI("TITAN_STATE", "BRAND_LOGO_DOWNLOAD_COMPLETED", null);
        this.resetLoader();
        this.removeComponent(AppLoader.PRELOADER);
        this.callHandler(this.preloaderReadyHandler);
        this.dispatchCore(LoaderEvent.PRELOADER_READY);
        this.loadLoader();
    }
    handlePreloaderComplete() {
        this.preloaderComplete = true;
        this.callHandler(this.preloaderCompleteHandler);
        this.handleComplete();
    }
    loadLoader() {
        if (this.hasComponent(AppLoader.LOADER)) {
            this.componentLoader = new ComponentLoader(this.request, AppLoader.LOADER);
            this.componentLoader.onComplete(this.handleLoaderReady, this);
            this.componentLoader.load([this.getComponent(AppLoader.LOADER)]);
        }
        else {
            this.handleLoaderReady();
        }
    }
    handleLoaderReady() {
        this.loaderReady = true;
        this.resetLoader();
        this.removeComponent(AppLoader.LOADER);
        this.handleComplete();
        this.loadGame();
    }
    loadGame() {
        this.componentLoader = new ComponentLoader(this.request, AppLoader.GAME);
        this.componentLoader.onProgress(this.handleLoadGameProgress, this);
        this.componentLoader.onComplete(this.handleLoadGameComplete, this);
        this.componentLoader.load(this.componentsByKey.getValues());
    }
    handleLoadGameProgress() {
        this.progress = this.componentLoader.getProgress();
        if (this.loadersComplete) {
            this.callHandler(this.loaderProgressHandler);
        }
    }
    handleLoadGameComplete() {
        this.gameComplete = true;
        this.handleComplete();
        this.dispatchKPI("TITAN_STATE", "BRAND_LOADING_PAGE_STARTED", null);
    }
    handleComplete() {
        this.handleLoadersComplete();
        this.handleGameComplete();
    }
    handleLoadersComplete() {
        if (this.preloaderComplete && this.loaderReady) {
            this.preloaderComplete = false;
            this.loaderReady = false;
            this.loadersComplete = true;
            this.callHandler(this.loaderReadyHandler);
            this.dispatchCore(LoaderEvent.LOADER_READY);
            this.renderer.setBackgroundColour(0x000000);
        }
    }
    handleGameComplete() {
        if (this.loadersComplete && this.gameComplete) {
            this.resetLoader();
            this.callHandler(this.loaderProgressHandler);
            this.callHandler(this.loaderCompleteHandler);
            this.reset();
            this.loadLazy();
        }
    }
    loadLazy() {
        if (ComponentLoader.LAZY.length > 0) {
            this.lazyLoader = new AssetLoader(ComponentLoader.LAZY);
            this.lazyLoader.load();
        }
    }
    unload() {
        this.componentUnloader.unload(this.components);
    }
    unloadComponent(name) {
        this.componentUnloader.unload([this.getComponent(name)]);
    }
    getProgress() {
        return this.progress;
    }
    hasComponent(id) {
        return this.componentsById.containsKey(id);
    }
    getComponent(id) {
        return this.componentsByKey.getValue(id);
    }
    removeComponent(id) {
        this.componentsById.remove(id);
        this.componentsByKey.remove(id);
    }
    callHandler(handler) {
        if (handler) {
            handler.call(this);
        }
    }
    listenCore(event, action, scope) {
        this.core.addEventListener(event.getType(), action, scope);
    }
    dispatchCore(event) {
        this.core.dispatchEvent(event);
    }
    async dispatchBlockingEvent(event) {
        this.core.dispatchBlockingEvent(event);
    }
    dispatchKPI(category, operation, value) {
        this.dispatchCore(new EventObject(CommonGameEvent.BI_KPI.getType(), {
            category: category,
            operation: operation,
            value: value
        }));
    }
}
AppLoader.PRELOADER = "Preloader";
AppLoader.LOADER = "Loader";
AppLoader.GAME = "Game";
//# sourceMappingURL=AppLoader.js.map